import { invariant } from "ts-invariant";
import { z } from "zod";

import type { BranchReview } from "../branches/changes";

/** A task action attached through the API; its script belongs to the project. */
export const attachmentSchema = z
  .object({
    title: z.string().min(1).max(200),
    script: z.string().min(1),
    jig: z.string().min(1),
    target: z.string().min(1),
    input: z.json(),
  })
  .strict();

export const artifactSchema = attachmentSchema.extend({
  digest: z.string(),
  bundle: z.string(),
  sources: z.record(z.string(), z.string()),
});

export const receiptSchema = z.object({
  revision: z.string(),
  evidence: z.json(),
});

const completed = { receipts: z.array(receiptSchema), finishedAt: z.string() };
export const scriptResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("succeeded") }),
  z.object({ status: z.literal("unknown"), message: z.string() }),
  z.object({ status: z.literal("interrupted"), message: z.string() }),
]);
export const executionStateSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending") }),
  z.object({
    status: z.literal("running"),
    startedAt: z.string(),
    userId: z.string(),
  }),
  z.object({
    status: z.enum(["submitting", "submitted"]),
    startedAt: z.string(),
    script: scriptResultSchema,
    receipts: z.array(receiptSchema),
  }),
  z.object({ status: z.literal("succeeded"), ...completed }),
  z.object({ status: z.literal("failed"), message: z.string(), ...completed }),
  z.object({ status: z.literal("unknown"), message: z.string(), ...completed }),
  /** Done, and read back as done, but not what review previewed: the message says how. */
  z.object({
    status: z.literal("differed"),
    message: z.string(),
    ...completed,
  }),
]);

export const executionSchema = z.object({
  artifact: artifactSchema,
  state: executionStateSchema,
});

export type Attachment = z.infer<typeof attachmentSchema>;
export type TaskActionRequest =
  | { method: "GET" | "DELETE" }
  | { method: "PUT"; attachment: Attachment };
export type Artifact = z.infer<typeof artifactSchema>;
export type Execution = z.infer<typeof executionSchema>;
export type ExecutionState = Execution["state"];
export type Receipt = z.infer<typeof receiptSchema>;
export type ScriptResult = z.infer<typeof scriptResultSchema>;
export type Verification =
  | { status: "verified" | "submitting" | "submitted"; receipts: Receipt[] }
  | {
      status: "failed" | "unknown" | "differed";
      message: string;
      receipts: Receipt[];
    };
export type Json = z.infer<ReturnType<typeof z.json>>;
export type TaskReview = BranchReview & { migration: Artifact | null };

export interface MigrationContext {
  input: Json;
  jig: { call(method: string, input: Json): Promise<Json> };
}

export function startExecution(
  state: ExecutionState,
  now: string,
  userId: string,
): ExecutionState {
  invariant(
    state.status === "pending",
    "An external action can only start once",
  );
  return { status: "running", startedAt: now, userId };
}

export function canCheckExecution(
  state: ExecutionState,
): state is Extract<
  ExecutionState,
  { status: "running" | "submitting" | "submitted" }
> {
  return (
    state.status === "running" ||
    state.status === "submitting" ||
    state.status === "submitted"
  );
}

/** Submission intent is not an outcome: keep it inspectable until acknowledged. */
export function settleExecution(
  execution: Execution,
  verification: Verification,
  script: ScriptResult,
  now: string,
): Execution {
  const state = execution.state;
  invariant(canCheckExecution(state), "Only a running action can finish");
  const receipts = verification.receipts;
  switch (verification.status) {
    case "submitting":
    case "submitted":
      return {
        ...execution,
        state: {
          status: verification.status,
          script,
          receipts,
          startedAt: state.startedAt,
        },
      };
    case "failed":
    case "unknown":
    case "differed":
      return {
        ...execution,
        state: {
          status: verification.status,
          message: verification.message,
          receipts,
          finishedAt: now,
        },
      };
    case "verified":
      return {
        ...execution,
        state:
          script.status === "succeeded"
            ? { status: "succeeded", receipts, finishedAt: now }
            : {
                status: script.status === "interrupted" ? "unknown" : "failed",
                message: script.message,
                receipts,
                finishedAt: now,
              },
      };
  }
}
